import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

export type ColombiaRail = 'COBREB' | 'CO';

export interface RailConfig {
  id: number;
  pais: string;
  rail: ColombiaRail;
  comisionPct: number | string;
  fijoUsd: number | string;
  costoFijoCop: number | string;
  /** 'A' = Modelo A xls (comisión propia) · 'ESPEJO' = igualar oferta Vita en vivo. */
  modoPricing?: 'A' | 'ESPEJO';
  /** Piso de utilidad USD por operación (ambos modos). */
  margenMinUsd?: number | string;
  modoTasa: 'AUTO' | 'MANUAL';
  tasaCostoManual: number | string | null;
  tasaManualVigenteHasta: string | null;
  motivo: string | null;
  responsible: string;
  vigenteDesde: string;
  vigenteHasta: string | null;
}

export interface UpsertRailConfig {
  rail: ColombiaRail;
  comisionPct: number;
  fijoUsd: number;
  costoFijoCop: number;
  modoPricing?: 'A' | 'ESPEJO';
  margenMinUsd?: number;
  modoTasa: 'AUTO' | 'MANUAL';
  tasaCostoManual?: number;
  tasaManualVigenteHasta?: string;
  motivo?: string;
}

export interface LiveRates {
  rails: Record<
    ColombiaRail,
    {
      partnerUsdSell: number;
      publicUsdSell: number | null;
      /** usd_sell PÚBLICO (oferta retail de Vita, insumo del ESPEJO). */
      publicSellUsd?: number | null;
      /** Fee retail de Vita en COP (hoy 3.000). */
      publicFeeCop?: number | null;
      fixedCostCop: number;
      businessDays: number;
      validUntil: string | null;
    } | null
  >;
  publicaStale: boolean;
  fetchedAt: string;
}

/** Simulación de la config del FORM (sin guardar) — tabla comparativa. */
export interface QuotePreviewRequest {
  montos: number[];
  modoPricing: 'A' | 'ESPEJO';
  comisionPct?: number;
  fijoUsd?: number;
  margenMinUsd?: number;
  costoFijoCop?: number;
}

export interface QuotePreviewItem {
  montoUsd: number;
  /** Lo que Vita retail entregaría HOY por este monto (null si feed caído). */
  ofertaVitaCop: number | null;
  quote: QuoteResult;
}

export interface QuotePreview {
  paisActivo: boolean;
  fetchedAt: string;
  publicaStale: boolean;
  rails: Record<ColombiaRail, { configurado: boolean; items: QuotePreviewItem[] }>;
}

export interface QuoteResult {
  disponible: boolean;
  motivo?: string;
  rail: ColombiaRail;
  paisActivo?: boolean;
  tasaSource?: 'AUTO' | 'MANUAL' | 'MANUAL_EXPIRED';
  /** Modo aplicado en ESTA cotización. */
  modoPricing?: 'A' | 'ESPEJO';
  /** ESPEJO configurado pero sin oferta retail viva → degradó a A. */
  espejoFallback?: boolean;
  stale?: boolean;
  publicaFallback?: boolean;
  /** false = ganancia bajo el piso → el cliente vería "no disponible". */
  margenOk?: boolean;
  businessDays?: number;
  validUntil?: string | null;
  resultado?: {
    montoUsd: number;
    tasaPublica: number;
    tasaPartner: number;
    /** ESPEJO: el piso de margen impidió igualar la oferta de Vita. */
    clamped?: boolean;
    /** ESPEJO: lo que Vita pública entregaría (referencia de igualación). */
    ofertaVitaCop?: number;
    spreadPct: number;
    comisionAguacateUsd: number;
    markupEfectivoPct: number;
    feeVitaCop: number;
    recibeCop: number;
    tasaEfectivaCliente: number;
    costoUsd: number;
    gananciaUsd: number;
  };
}

export interface RegistroVitaTx {
  txId: number;
  fecha: string;
  status: string;
  rail: string;
  origen: { pais: string | null; moneda: string | null };
  destino: { pais: string | null; moneda: string | null };
  remitente: { usuarioId: number; nombre: string | null; email: string | null };
  beneficiario: string | null;
  beneficiarioDetalle: {
    nombre: string | null;
    banco: string | null;
    cuentaMask: string | null;
    tipoId: string | null;
    numeroId: string | null;
    variante: string | null;
    pais: string | null;
    vitaEstado: string | null;
  } | null;
  montoUsd: number;
  recibeCop: number;
  tasaEfectiva: number;
  /**
   * Moneda de destino de la fila (multi-rail: 'COP' o 'PEN'). `recibe` es el
   * monto en ESA moneda ya normalizado (PEN sale de céntimos), y `decimales`
   * los decimales para formatearlo (COP=0, PEN=2). El front muestra cada fila en
   * su moneda con estos campos (fallback a COP para filas viejas sin ellos).
   */
  moneda?: string;
  recibe?: number;
  decimales?: number;
  /** Monto en la moneda que pagó el cliente (CLP o USDT). USDT→COP = montoUsd. */
  montoOrigen: number;
  /** Tasa COP por unidad de origen (CLP→COP: COP/CLP; USDT→COP: COP/USD). */
  tasaCliente: number;
  vitaTxId: string | null;
  /** Detalle de resolución (operador): motivo de rechazo/revisión, intentos. */
  errorDetail: string | null;
  verificationAttempts: number;
  completedAt: string | null;
  /** Estado de `transacciones` (25/10/2026): ANULADA se muestra aparte de Rechazada. */
  txEstado?: string | null;
  /**
   * Remesas con origen CLP en revisión (25/10/2026): true si wallet-back ya
   * escribió su débito en `balances`, false si no; null si no aplica o no se
   * pudo consultar. Decide Rechazar y revertir / Reanudar / Anular.
   */
  debitoClp?: boolean | null;
  /** Datos internos (NO cliente). */
  interno: {
    tasaPublica: number | null;
    /** Puente CLP→USD (solo CLP→COP): tasa de venta + USD intermedio. */
    origenUsdRate?: number | null;
    montoUsd?: number;
    tasaPartner: number | null;
    /** Ganancia ESTIMADA al cotizar. */
    gananciaUsd: number | null;
    /** Ganancia REALIZADA (montoUsd − USD enviados a Vita); null si no se envió o se revirtió. */
    gananciaRealUsd: number | null;
    comisionFeeCop: number | null;
  };
}

export interface RegistroVita {
  saldoUsd: number;
  transacciones: RegistroVitaTx[];
}

// ── Config CLP→COP (pata CLP→USD; singleton por país, versionada) ──

export type ClpTasaSource = 'LIVE_SELL' | 'SPOT' | 'MANUAL';

export interface ClpCopConfig {
  id: number;
  pais: string;
  tasaSource: ClpTasaSource;
  tasaManual: number | string | null;
  tasaManualVigenteHasta: string | null;
  markupPct: number | string;
  minClp: number | string;
  motivo: string | null;
  responsible: string;
  vigenteDesde: string;
  vigenteHasta: string | null;
}

export interface UpsertClpCopConfig {
  tasaSource: ClpTasaSource;
  tasaManual?: number;
  tasaManualVigenteHasta?: string;
  markupPct?: number;
  minClp?: number;
  motivo?: string;
}

/** Switchboard de rieles activos hacia COP (parametros RAILES_ACTIVOS). */
export interface RailesActivos {
  cop: { usdt_cop: boolean; clp_cop: boolean; ves_cop: boolean };
}

/** Tasa CLP/USD resuelta: base (fuente) + efectiva (con markup). */
export interface ClpUsdRate {
  base: number;
  efectiva: number;
  source: ClpTasaSource;
  stale: boolean;
  markupPct: number;
}

export interface ClpLiveRates {
  /** Spot USD/CLP (PRECIO_SPOT). */
  spot: number | null;
  /** Última tasa de venta CLP→USDT (la que ve el cliente hoy). */
  liveSell: number | null;
  /** Tasa resuelta según la fuente activa + markup. */
  rate: ClpUsdRate | null;
  activo: boolean;
  minClp: number;
  fetchedAt: string;
}

export interface ClpPreviewItem {
  montoClp: number;
  montoUsd: number;
  cumpleMinimo: boolean;
  recibeCop: number;
  /** Tasa única que ve el cliente: COP por CLP. */
  tasaEfectivaCopClp: number;
  gananciaUsd: number;
  disponible: boolean;
  margenOk: boolean;
  /** Lo que Vita retail daría (COP) — benchmark de la pata USD→COP (ESPEJO). */
  ofertaVitaCop: number | null;
  /** Esa oferta de Vita en COP/CLP. */
  tasaVitaCopClp: number | null;
  /** ESPEJO no pudo igualar a Vita por el piso → damos menos que Vita. */
  clamped: boolean;
  /** ESPEJO degradado (sin oferta retail viva). */
  espejoFallback: boolean;
  /** Feed/tasa de Vita stale al cotizar. */
  stale: boolean;
}

export interface ClpPreview {
  activo: boolean;
  rate: ClpUsdRate | null;
  minClp: number;
  fetchedAt: string;
  rails: Record<ColombiaRail, ClpPreviewItem[]>;
}

/** Override del form (sin guardar) para simular en el preview. */
export interface ClpPreviewOverride {
  tasaSource?: ClpTasaSource;
  tasaManual?: number;
  tasaManualVigenteHasta?: string;
  markupPct?: number;
  minClp?: number;
}

// ─── VES→COP (pata VES→USD; espejo de CLP, sin SPOT) ───

export type VesTasaSource = 'AUTO' | 'MANUAL';

export interface VesCopConfig {
  id: number;
  pais: string;
  tasaSource: VesTasaSource;
  tasaManual: number | string | null;
  tasaManualVigenteHasta: string | null;
  markupPct: number | string;
  minVes: number | string;
  motivo: string | null;
  responsible: string;
  vigenteDesde: string;
  vigenteHasta: string | null;
}

export interface UpsertVesCopConfig {
  tasaSource: VesTasaSource;
  tasaManual?: number;
  tasaManualVigenteHasta?: string;
  markupPct?: number;
  minVes?: number;
  motivo?: string;
}

/** Tasa VES/USDT resuelta: base (fuente) + efectiva (con markup). */
export interface VesUsdRate {
  base: number;
  efectiva: number;
  source: VesTasaSource;
  stale: boolean;
  markupPct: number;
}

export interface VesLiveRates {
  /** Última `ves_usdt_rates.tasa_final` (VES por USDT). */
  liveVes: number | null;
  /** Tasa resuelta según la fuente activa + markup. */
  rate: VesUsdRate | null;
  activo: boolean;
  minVes: number;
  fetchedAt: string;
}

export interface VesPreviewItem {
  montoVes: number;
  montoUsd: number;
  cumpleMinimo: boolean;
  recibeCop: number;
  /** Tasa única que ve el cliente: COP por VES. */
  tasaEfectivaCopVes: number;
  gananciaUsd: number;
  disponible: boolean;
  margenOk: boolean;
  ofertaVitaCop: number | null;
  tasaVitaCopVes: number | null;
  clamped: boolean;
  espejoFallback: boolean;
  stale: boolean;
}

export interface VesPreview {
  activo: boolean;
  rate: VesUsdRate | null;
  minVes: number;
  fetchedAt: string;
  rails: Record<ColombiaRail, VesPreviewItem[]>;
}

export interface VesPreviewOverride {
  tasaSource?: VesTasaSource;
  tasaManual?: number;
  tasaManualVigenteHasta?: string;
  markupPct?: number;
  minVes?: number;
}

/**
 * Cliente del módulo Rail Colombia del backend (`/colombia`). Token Cognito lo
 * agrega ApiRestInterceptor.
 */
@Injectable({ providedIn: 'root' })
export class ColombiaService {
  private readonly API = environment.apiNest;

  constructor(private http: HttpClient) {}

  getConfig(): Promise<Record<ColombiaRail, RailConfig | null>> {
    return firstValueFrom(
      this.http.get<Record<ColombiaRail, RailConfig | null>>(
        `${this.API}colombia/config`,
      ),
    );
  }

  getConfigHistory(rail: ColombiaRail): Promise<RailConfig[]> {
    return firstValueFrom(
      this.http.get<RailConfig[]>(
        `${this.API}colombia/config/history?rail=${rail}`,
      ),
    );
  }

  upsertConfig(dto: UpsertRailConfig): Promise<RailConfig> {
    return firstValueFrom(
      this.http.post<RailConfig>(`${this.API}colombia/config`, dto),
    );
  }

  getLive(): Promise<LiveRates> {
    return firstValueFrom(this.http.get<LiveRates>(`${this.API}colombia/live`));
  }

  quote(rail: ColombiaRail, montoUsd: number): Promise<QuoteResult> {
    return firstValueFrom(
      this.http.post<QuoteResult>(`${this.API}colombia/quote`, {
        rail,
        montoUsd,
      }),
    );
  }

  /**
   * Preview de config (read-only): cotiza varios montos en AMBOS rieles con la
   * config del form sin persistir + la oferta retail de Vita por monto.
   */
  quotePreview(req: QuotePreviewRequest): Promise<QuotePreview> {
    return firstValueFrom(
      this.http.post<QuotePreview>(`${this.API}colombia/quote-preview`, req),
    );
  }

  /** Registro Vita (operador): saldo USD + transacciones con estado. */
  registro(): Promise<RegistroVita> {
    return firstValueFrom(
      this.http.get<RegistroVita>(`${this.API}colombia/registro`),
    );
  }

  // ── Config CLP→COP ──

  getClpConfig(): Promise<ClpCopConfig | null> {
    return firstValueFrom(
      this.http.get<ClpCopConfig | null>(`${this.API}colombia/clp/config`),
    );
  }

  getClpConfigHistory(): Promise<ClpCopConfig[]> {
    return firstValueFrom(
      this.http.get<ClpCopConfig[]>(`${this.API}colombia/clp/config/history`),
    );
  }

  upsertClpConfig(dto: UpsertClpCopConfig): Promise<ClpCopConfig> {
    return firstValueFrom(
      this.http.post<ClpCopConfig>(`${this.API}colombia/clp/config`, dto),
    );
  }

  /** Switchboard de rieles activos hacia COP (on/off que se ofrece al cliente). */
  getRailesActivos(): Promise<RailesActivos> {
    return firstValueFrom(
      this.http.get<RailesActivos>(`${this.API}colombia/railes-activos`),
    );
  }

  setRailesActivos(patch: {
    usdtCop?: boolean;
    clpCop?: boolean;
    vesCop?: boolean;
  }): Promise<RailesActivos> {
    return firstValueFrom(
      this.http.post<RailesActivos>(`${this.API}colombia/railes-activos`, patch),
    );
  }

  /** Tasa CLP/USD viva (spot + sell + resuelta por la fuente activa). */
  getClpLive(): Promise<ClpLiveRates> {
    return firstValueFrom(
      this.http.get<ClpLiveRates>(`${this.API}colombia/clp/live`),
    );
  }

  /** Preview CLP→COP: montos en CLP → COP por riel + tasa efectiva + ganancia. */
  clpQuotePreview(
    montosClp: number[],
    override?: ClpPreviewOverride,
  ): Promise<ClpPreview> {
    return firstValueFrom(
      this.http.post<ClpPreview>(`${this.API}colombia/clp/quote-preview`, {
        montosClp,
        ...(override ?? {}),
      }),
    );
  }

  // ─── Config VES→COP (pata VES→USD; espejo de CLP) ───

  getVesConfig(): Promise<VesCopConfig | null> {
    return firstValueFrom(
      this.http.get<VesCopConfig | null>(`${this.API}colombia/ves/config`),
    );
  }

  getVesConfigHistory(): Promise<VesCopConfig[]> {
    return firstValueFrom(
      this.http.get<VesCopConfig[]>(`${this.API}colombia/ves/config/history`),
    );
  }

  upsertVesConfig(dto: UpsertVesCopConfig): Promise<VesCopConfig> {
    return firstValueFrom(
      this.http.post<VesCopConfig>(`${this.API}colombia/ves/config`, dto),
    );
  }

  /** Tasa VES/USDT viva (tasa_final + resuelta por la fuente activa + markup). */
  getVesLive(): Promise<VesLiveRates> {
    return firstValueFrom(
      this.http.get<VesLiveRates>(`${this.API}colombia/ves/live`),
    );
  }

  /** Preview VES→COP: montos en VES → COP por riel + tasa efectiva + ganancia. */
  vesQuotePreview(
    montosVes: number[],
    override?: VesPreviewOverride,
  ): Promise<VesPreview> {
    return firstValueFrom(
      this.http.post<VesPreview>(`${this.API}colombia/ves/quote-preview`, {
        montosVes,
        ...(override ?? {}),
      }),
    );
  }

  /**
   * Resolución manual (WS3.1): cierra una remesa en REQUIRES_HUMAN_REVIEW tras
   * verificar en el panel de Vita. ENVIADA = confirmada (sin reversa);
   * RECHAZADA = no salió → reversa USDT al cliente. POST (no PATCH — CORS).
   */
  resolverRemesa(
    txId: number,
    resultado: 'ENVIADA' | 'RECHAZADA' | 'REANUDAR' | 'ANULAR',
    motivo?: string,
  ): Promise<{ txId: number; resultado: string }> {
    return firstValueFrom(
      this.http.post<{ txId: number; resultado: string }>(
        `${this.API}colombia/remesa-co/${txId}/resolver`,
        { resultado, ...(motivo ? { motivo } : {}) },
      ),
    );
  }
}
